import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Dates } from 'src/app/utils/calendar-utils';
import { SpinnerService } from 'src/app/utils/services/spinner.service';
import {Subscription, forkJoin, switchMap, tap, map, of, catchError} from "rxjs";
import {CourseFilterComponent} from "../../../../utils/course-filter/course-filter.component";
import { ProductContextService } from 'src/app/col/services/product-context.service';
import * as LessonsV3 from '../../../model/v3/lessons';
import {NativeServiceApiProvider} from "../../../../services/native-api-provider.service";
import {AuthorizationServiceProvider} from "../../../../auth_profile/services/authorization-service.provider";
import {ColNativeV3Api} from "../../../services/api/v3/col-native-v3-api";

@Component({
    selector: 'app-progress-card',
    templateUrl: './progress-card.component.html',
    styleUrls: ['./progress-card.component.scss'],
    standalone: false
})
export class ProgressCardComponent implements OnInit, OnDestroy {
  private _studentId: number;
  private _lang: string;
  private schoolId: number;
  private courseContexts: LessonsV3.ColCourseContext[] = [];
  private productContext: LessonsV3.ProductContextDetails | null;
  private lessonDatesById = new Map<number, Date | null>();
  private courseNamesByCode = new Map<string, string>();
  private currentIndex: number = null;
  isLoading = true;
  private _showNoContext = true
  CourseFilterComponent = CourseFilterComponent
  productContextSubscription: Subscription
  authSubscription: Subscription

  @Input()
  set studentId(value: number) {
    this._studentId = value;
    this.fireUpdate();
  }
  get studentId() {
    return this._studentId;
  }
  @Input()
  public set langCode(lang: string) {
    this._lang = lang;
    this.fireUpdate();
  }
  public get langCode() {
    return this._lang
  }
  @Input()
  public set showNoContext(showNoContext: boolean) {
    this._showNoContext = showNoContext;
  }
  get showNoContext() {
    return this._showNoContext
  }

  constructor(
    private authService: AuthorizationServiceProvider,
    private provider: NativeServiceApiProvider,
    private spinner: SpinnerService,
    private productContextService: ProductContextService
  ) {}

  ngOnInit(): void {
    this.authSubscription = this.authService.getAuthDetailsService().pipe(
      switchMap((api) => api.getSelfSchoolId()),
      tap((schoolId) => {
        this.schoolId = schoolId;
        this.fireUpdate();
      })
    ).subscribe();
    this.productContextSubscription = this.productContextService.productContext.subscribe(_ => this.fireUpdate())
  }

  ngOnDestroy(): void {
    if(this.productContextSubscription) this.productContextSubscription.unsubscribe()
    if(this.authSubscription) this.authSubscription.unsubscribe()
  }

  fireUpdate() {
    if (!this.studentId || !this.langCode || !this.schoolId) return;
    this.spinner
      .trace(this.loadProgressData())
      .subscribe({
        next: ({productContexts, courseContexts, courses, lessonDatesById}) => {
          this.afterDataLoad(
            productContexts?.[0] ?? null,
            courseContexts,
            courses,
            lessonDatesById
          );
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  private loadProgressData() {
    return this.provider.colV3().pipe(
      switchMap((api) =>
        api.getProvaContext(this.schoolId, this.studentId, this.langCode).pipe(
          switchMap(() =>
            forkJoin({
              productContexts: api.getProductContexts(
                this.schoolId,
                this.studentId,
                this.langCode
              ),
              courseContexts: api.getCourseContexts(
                this.schoolId,
                this.studentId,
                this.langCode
              ),
              courses: api.getProductCourses(this.schoolId, this.langCode),
            })
          ),
          switchMap(({productContexts, courseContexts, courses}) => {
            const safeCourseContexts = courseContexts ?? [];
            return this.loadLessonDates(api, safeCourseContexts).pipe(
              map((lessonDatesById) => ({
                productContexts,
                courseContexts: safeCourseContexts,
                courses: courses ?? [],
                lessonDatesById,
              }))
            );
          })
        )
      )
    );
  }

  private afterDataLoad(
    context: LessonsV3.ProductContextDetails | null,
    courseContexts: LessonsV3.ColCourseContext[],
    courses: LessonsV3.Course[],
    lessonDatesById: Map<number, Date | null>
  ) {
    this.isLoading = false;
    this.productContext = context
    this.courseContexts = (courseContexts ?? []).slice().sort((a, b) =>
      (a.course?.code ?? '').localeCompare(b.course?.code ?? '', undefined, {numeric: true})
    );
    this.lessonDatesById = lessonDatesById;
    this.courseNamesByCode = new Map(courses.map((course) => [course.code, course.name]));
    this.currentIndex = this.courseContexts.length ? this.courseContexts.length - 1 : null;
  }

  private collectLessonIds(courseContexts: LessonsV3.ColCourseContext[]): number[] {
    const ids = new Set<number>();
    const refs = courseContexts.flatMap((context) => [
      context.firstLesson,
      context.lastExam,
      context.lastQuickStageRevision,
      context.lastRegularLesson,
      context.lastRevision,
    ]);

    refs.forEach((ref) => {
      if (ref && ref.id > 0) {
        ids.add(ref.id);
      }
    });

    return Array.from(ids);
  }

  private loadLessonDates(
    api: ColNativeV3Api,
    courseContexts: LessonsV3.ColCourseContext[]
  ) {
    const lessonIds = this.collectLessonIds(courseContexts);
    if (lessonIds.length === 0) {
      return of(new Map<number, Date | null>());
    }

    return forkJoin(
      lessonIds.map((lessonId) =>
        api.getLesson(lessonId, 'preview').pipe(
          map((lesson) => {
            const started = lesson?.lessonMetric?.started ?? null;
            const plannedStart = lesson?.lessonMetric?.plannedStartDate ?? null;
            const dateValue = started ?? plannedStart;
            return [
              lessonId,
              dateValue
                ? (dateValue instanceof Date ? dateValue : Dates.parse(String(dateValue)))
                : null,
            ] as [number, Date | null];
          }),
          catchError(() => of([lessonId, null] as [number, Date | null]))
        )
      )
    ).pipe(map((entries) => new Map<number, Date | null>(entries)));
  }

  moveCourse(number: number) {
    this.currentIndex += number;
  }

  private getContext(): LessonsV3.ColCourseContext {
    return this.courseContexts[this.currentIndex];
  }

  private widthStyle(width: number) {
    return { width: `${width}%` };
  }

  private getLessonStartDate(lesson: LessonsV3.LessonRef | null | undefined) {
    if (!lesson || !lesson.id || lesson.id <= 0)
      return null;
    return this.lessonDatesById.get(lesson.id) ?? null;
  }

  getCurrentCourseName() {
    const courseCode = this.getContext().course.code;
    return this.courseNamesByCode.get(courseCode) ?? courseCode;
  }

  getProgress() {
    return this.getContext().lessonsProgress
      ? this.getContext().lessonsProgress * 100
      : 0;
  }

  getRevisionProgress() {
    return this.getContext().revisionProgress
      ? this.getContext().revisionProgress * 100
      : 0;
  }

  getExamProgressWidth() {
    const progress = this.getContext().exams ? 100 : 0;
    return this.widthStyle(progress);
  }

  getRevisionProgressWidth() {
    const progress = this.getRevisionProgress();
    return this.widthStyle(progress);
  }

  getLessonProgressWidth() {
    const progress = this.getProgress();
    return this.widthStyle(progress);
  }

  getExamProgressDescription() {
    return this.getContext().exams ? '✓' : '---';
  }

  getFirstLessonDate() {
    return this.getLessonStartDate(this.getContext().firstLesson);
  }

  getLastLessonDate() {
    const allDates = this.getContextDates(this.getContext())
      .filter((el) => el != null)
      .sort((l, r) => l.getTime() - r.getTime());
    if (allDates.length === 0) return null;
    return allDates[allDates.length - 1];
  }

  private getContextDates(context: LessonsV3.ColCourseContext) {
    return [
      this.getLessonStartDate(context.lastExam),
      this.getLessonStartDate(context.lastQuickStageRevision),
      this.getLessonStartDate(context.lastRegularLesson),
      this.getLessonStartDate(context.lastRevision),
    ];
  }

  getLastExamDate() {
    return this.getLessonStartDate(this.getContext().lastExam);
  }

  getNumberOverallLessons() {
    return this.getContext().overall;
  }

  hasAnyProgress() {
    return this.currentIndex != null;
  }

  hasAnyLesson() {
    return this.getContext().lessons > 0;
  }

  hasPrev() {
    return this.currentIndex != null && this.currentIndex > 0;
  }

  hasNext() {
    return (
      this.currentIndex != null &&
      this.currentIndex < this.courseContexts.length - 1
    );
  }

  isDisplayed() {
    return this.courseContexts?.length > 0 || this.showNoContext
  }

  isCourseActive() {
    return !!this.productContext
  }

  isKidsCourse() {
    return this.langCode?.startsWith('en.ch') ?? false;
  }
}
